import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Screen } from '../components/Layout'
import {
  Balance,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Spinner,
  balanceSentence,
  inputClass,
} from '../components/ui'
import { useInviteMember, useMe, type InviteResult } from '../lib/queries'

/**
 * The people you split with, and the invite form (requirement 5).
 *
 * An invite is addressed to an email, and the pair exists from that moment --
 * before the other person has an account. Splits recorded against a 待加入 member
 * are real and appear in their list the first time they sign in, so the status
 * badge explains the wait rather than blocking the split.
 */
export function MembersPage() {
  const me = useMe()
  const invite = useInviteMember()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [open, setOpen] = useState(false)
  const [outcome, setOutcome] = useState<InviteResult>()

  const members = me.data?.members ?? []
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && displayName.trim() !== ''

  return (
    <Screen
      title="分帳成員"
      action={
        members.length > 0 ? (
          <button type="button" className="text-sm text-accent" onClick={() => setOpen(!open)}>
            {open ? '取消' : '邀請'}
          </button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {me.isLoading ? <Spinner /> : null}
        {me.error ? <ErrorBanner error={me.error} onRetry={() => void me.refetch()} /> : null}

        {(open || members.length === 0) && !me.isLoading ? (
          <Card className="space-y-3 p-4">
            <ErrorBanner error={invite.error} />
            <Field label="對方的 Google 帳號 Email">
              <input
                type="email"
                className={inputClass}
                inputMode="email"
                autoCapitalize="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="someone@gmail.com"
              />
            </Field>
            <Field label="顯示名稱" hint="只有你看得到，用來在分帳畫面上稱呼對方。">
              <input
                className={inputClass}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="小明"
              />
            </Field>
            <Button
              className="w-full"
              disabled={!valid || invite.isPending}
              onClick={() =>
                invite.mutate(
                  { email: email.trim().toLowerCase(), displayName: displayName.trim() },
                  {
                    onSuccess: (result) => {
                      setEmail('')
                      setDisplayName('')
                      setOpen(false)
                      setOutcome(result)
                    },
                  },
                )
              }
            >
              {invite.isPending ? '邀請中…' : '送出邀請'}
            </Button>
            <p className="text-xs text-muted">
              對方登入過 VMoney 就會立刻配對；還沒登入過也沒關係，等他第一次用同一個 Email
              以 Google 登入就會自動加入，在那之前你也可以先記分帳。
            </p>
          </Card>
        ) : null}

        {/*
          The invite is silent -- VMoney sends no mail -- so this is the only place
          the inviter learns which of the two things just happened. Without it,
          "linked immediately" and "waiting for their first sign-in" look identical.
        */}
        {outcome ? (
          <Card className="p-4 text-sm">
            <p>{inviteOutcomeText(outcome)}</p>
            <button
              type="button"
              className="mt-2 text-xs text-accent"
              onClick={() => setOutcome(undefined)}
            >
              好
            </button>
          </Card>
        ) : null}

        {members.length === 0 && !me.isLoading && !open ? (
          <EmptyState title="還沒有分帳成員" hint="邀請一個人之後，記帳時就能選擇分帳。" />
        ) : null}

        <ul className="space-y-2">
          {members.map((member) => (
            <li key={member.pairId}>
              <Link
                to={`/members/${member.pairId}`}
                className="flex items-center gap-3 rounded-2xl bg-ink-soft/70 p-4 ring-1 ring-ink-line/60"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate">
                    {member.displayName}
                    {member.status === 'pending' ? (
                      <span className="ml-2 rounded bg-ink px-1.5 py-0.5 text-xs text-muted">
                        待加入
                      </span>
                    ) : null}
                  </p>
                  {member.status === 'pending' ? (
                    // The badge on its own reads like "waiting for them to accept",
                    // and there is nothing to accept -- so say what is actually
                    // being waited on.
                    <p className="truncate text-xs text-muted">
                      對方還沒用那個 Email 登入過，登入後會自動配對
                    </p>
                  ) : null}
                  <p className="truncate text-xs text-muted">
                    {balanceSentence(member.netMinor, member.currency, member.displayName)}
                    {member.openSplitCount > 0 ? ` · ${member.openSplitCount} 筆未結` : ''}
                  </p>
                </div>
                <Balance netMinor={member.netMinor} currency={member.currency} />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Screen>
  )
}

function inviteOutcomeText(result: InviteResult): string {
  if (result.alreadyExisted) return `${result.displayName} 已經在你的分帳清單裡了。`
  if (result.status === 'linked') return `已經和 ${result.displayName} 配對成功，可以開始分帳了。`
  return `已經記下 ${result.displayName}。對方第一次用那個 Email 登入 VMoney 後就會自動配對——VMoney 不會寄信，請自己告訴他一聲。`
}
